import { defineComponent as _defineComponent } from 'vue'
import {
  ref,
  onMounted,
  onUnmounted,
  Ref,
  watch,
  computed,
  withDefaults,
} from 'vue';
import RoomHeader from './components/RoomHeader/index';
import RoomFooter from './components/RoomFooter/index';
import RoomSidebar from './components/RoomSidebar';
import RoomContent from './components/RoomContent/index.vue';
import RoomSetting from './components/RoomSetting/index.vue';
import LoadingOverlay from './components/PreRoom/LoadingOverlay.vue';
import PasswordDialog from './components/PreRoom/PasswordDialog.vue';
import AISubtitlesOverlay from './components/AITools/AISubtitles.vue';
import RoomInviteOverlay from './components/RoomInvite';
import { debounce, throttle } from './utils/utils';
import { useBasicStore } from './stores/basic';
import { isMobile, isWeChat } from './utils/environment';
import './directives/vTap';
import {
  TUIKickedOutOfRoomReason,
  TUIErrorCode,
} from '@tencentcloud/tuiroom-engine-js';

import { MESSAGE_DURATION } from './constants/message';

import TUIMessageBox from './components/common/base/MessageBox/index';
import TUIMessage from './components/common/base/Message/index';
import useTRTCDetect from './hooks/useTRTCDetect';
import {
  roomService,
  EventType,
  RoomParam,
  RoomInitData,
} from './services/index';
import useDeviceManager from './hooks/useDeviceManager';
import useCustomizedAutoPlayDialog from './hooks/useCustomizedAutoPlayDialog';


export default /*#__PURE__*/_defineComponent({
  __name: 'conference',
  props: {
    displayMode: { default: 'permanent' }
  },
  emits: [
  'on-log-out',
  'on-create-room',
  'on-enter-room',
  'on-exit-room',
  'on-destroy-room',
  'on-kicked-out-of-room',
  'on-kicked-off-line',
  'on-userSig-expired',
],
  setup(__props: any, { expose, emit }) {

const props = __props as { displayMode: 'permanent' | 'wake-up' };

const isShowPasswordContainer = ref(false);
const isShowLoading = ref(true);


const overlayMap = ref<{
  AISubtitlesOverlay: { visible: boolean };
  RoomInviteOverlay: { visible: boolean };
  [key: string]: { visible: boolean };
}>({
  AISubtitlesOverlay: { visible: false },
  RoomInviteOverlay: { visible: false },
});

const conferenceShow = computed(
  () => props.displayMode === 'permanent' || !!basicStore.roomId
);

useCustomizedAutoPlayDialog();
useTRTCDetect();
useDeviceManager({ listenForDeviceChange: true });

const { t } = roomService;
expose({
  init,
  createRoom,
  enterRoom,
  dismissRoom,
  leaveRoom,
  resetStore,
  t,
});



const basicStore = useBasicStore();

const showMessageBox = (data: {
  code?: number;
  message: string;
  title: string;
  duration?: number;
  cancelButtonText: string;
  confirmButtonText: string;
  callback?: () => void;
}) => {
  const {
    message,
    title = roomService.t('Note'),
    duration,
    cancelButtonText,
    confirmButtonText = roomService.t('Sure'),
    callback = () => {},
  } = data;
  TUIMessageBox({
    title,
    message,
    duration,
    cancelButtonText,
    confirmButtonText,
    callback,
  });
};
const showMessage = (data: {
  type: 'warning' | 'success' | 'error' | 'info';
  message: string;
  duration: MESSAGE_DURATION;
}) => {
  const { type, message, duration } = data;
  TUIMessage({
    type,
    message,
    duration,
  });
};

const handleShowOverlay = (data: { name: string; visible: boolean }) => {
  if (overlayMap.value[data.name]) {
    overlayMap.value[data.name].visible = data.visible;
  }
};

onMounted(() => {
  roomService.on(EventType.ROOM_NOTICE_MESSAGE, showMessage);
  roomService.on(EventType.ROOM_NOTICE_MESSAGE_BOX, showMessageBox);
  roomService.on(EventType.KICKED_OUT, onKickedOutOfRoom);
  roomService.on(EventType.USER_SIG_EXPIRED, onUserSigExpired);
  roomService.on(EventType.KICKED_OFFLINE, onKickedOffLine);
  roomService.on(EventType.ROOM_START, onStartRoom);
  roomService.on(EventType.ROOM_JOIN, onJoinRoom);
  roomService.on(EventType.ROOM_LEAVE, onLeaveRoom);
  roomService.on(EventType.ROOM_DISMISS, onDismissRoom);
  roomService.on(EventType.USER_LOGOUT, onLogout);
  roomService.on(EventType.ROOM_NEED_PASSWORD, onRoomNeedPassword);
});
onUnmounted(() => {
  roomService.off(EventType.ROOM_NOTICE_MESSAGE, showMessage);
  roomService.off(EventType.ROOM_NOTICE_MESSAGE_BOX, showMessageBox);
  roomService.off(EventType.KICKED_OUT, onKickedOutOfRoom);
  roomService.off(EventType.USER_SIG_EXPIRED, onUserSigExpired);
  roomService.off(EventType.KICKED_OFFLINE, onKickedOffLine);
  roomService.off(EventType.ROOM_START, onStartRoom);
  roomService.off(EventType.ROOM_JOIN, onJoinRoom);
  roomService.off(EventType.ROOM_LEAVE, onLeaveRoom);
  roomService.off(EventType.ROOM_DISMISS, onDismissRoom);
  roomService.off(EventType.USER_LOGOUT, onLogout);
  roomService.off(EventType.ROOM_NEED_PASSWORD, onRoomNeedPassword);
  roomService.resetStore();
});

const { showHeaderTool } = roomService.basicStore;
const tuiRoomClass = computed(() => {
  const roomClassList = [
    'tui-room',
    `tui-theme-${roomService.basicStore.defaultTheme}`,
  ];
  if (isMobile) {
    roomClassList.push('tui-room-h5');
  }
  if (basicStore.scene === 'chat') {
    roomClassList.push('chat-room');
  }
  return roomClassList;
});

/**
 * Handle page mouse hover display toolbar logic
 *
 **/
const roomContentRef = ref<InstanceType<typeof RoomContent>>();
const showRoomTool: Ref<boolean> = ref(true);
const roomRef: Ref<Node | undefined> = ref();
function handleHideRoomTool() {
  showRoomTool.value = false;
}

watch(
  () => roomRef.value,
  (newValue, oldValue) => {
    if (!isWeChat && !isMobile) {
      if (newValue) {
        addRoomContainerEvent(newValue);
      } else {
        oldValue && removeRoomContainerEvent(oldValue);
      }
    }
  }
);

const handleHideRoomToolDebounce = debounce(handleHideRoomTool, 5000);
const handleHideRoomToolThrottle = throttle(handleHideRoomToolDebounce, 1000);
const showTool = () => {
  showRoomTool.value = true;
  handleHideRoomToolDebounce();
};
const showToolThrottle = () => {
  showRoomTool.value = true;
  handleHideRoomToolThrottle();
};
const hideTool = () => {
  handleHideRoomTool();
};
const addRoomContainerEvent = (container: Node) => {
  container.addEventListener('mouseenter', showTool);
  container.addEventListener('click', showTool);
  container.addEventListener('mousemove', showToolThrottle);
  container.addEventListener('mouseleave', hideTool);
};
const removeRoomContainerEvent = (container: Node) => {
  container.removeEventListener('mouseenter', showTool);
  container.removeEventListener('click', showTool);
  container.removeEventListener('mousemove', showToolThrottle);
  container.removeEventListener('mouseleave', hideTool);
};

function handleRoomContentTap() {
  showRoomTool.value = !showRoomTool.value;
  if (showRoomTool.value) {
    handleHideRoomToolDebounce();
  }
}

async function dismissRoom() {
  await roomService.dismissRoom();
}

async function leaveRoom() {
  await roomService.leaveRoom();
}

async function init(option: RoomInitData) {
  await roomService.initRoomKit(option);
}

async function createRoom(options: {
  roomId: string;
  roomName: string;
  roomMode: 'FreeToSpeak' | 'SpeakAfterTakingSeat';
  roomParam?: RoomParam;
}) {
  const { roomId, roomName, roomMode, roomParam } = options;
  roomService.createRoom({
    roomId,
    roomName,
    roomMode,
    roomParam,
  });
}

async function enterRoom(options: { roomId: string; roomParam?: RoomParam }) {
  await roomService.enterRoom(options);
}

function onRoomNeedPassword(code: TUIErrorCode) {
  if (code === TUIErrorCode.ERR_NEED_PASSWORD) {
    isShowPasswordContainer.value = true;
  }
}

function resetStore() {
  roomService.resetStore();
}

const logOut = () => {
  roomService.logOut();
};

const onStartRoom = () => {
  isShowLoading.value = false;
  emit('on-create-room', { code: 0, message: 'create room' });
};

const onJoinRoom = () => {
  isShowLoading.value = false;
  isShowPasswordContainer.value = false;
  emit('on-enter-room', { code: 0, message: 'enter room' });
};

const onLeaveRoom = () => {
  emit('on-exit-room', { code: 0, message: 'exit room' });
};

const onDismissRoom = () => {
  emit('on-destroy-room', { code: 0, message: 'destroy room' });
};

const onLogout = () => {
  emit('on-log-out', { code: 0, message: 'user logout' });
};

const onKickedOutOfRoom = async (eventInfo: {
  roomId: string;
  reason: TUIKickedOutOfRoomReason;
  message: string;
}) => {
  const { roomId, reason, message } = eventInfo;
  emit('on-kicked-out-of-room', { roomId, reason, message });
};

const onUserSigExpired = () => {
  emit('on-userSig-expired');
};

const onKickedOffLine = (eventInfo: { message: string }) => {
  const { message } = eventInfo;
  emit('on-kicked-off-line', { message });
};

return { __sfc: true,isShowPasswordContainer, isShowLoading, props, overlayMap, conferenceShow, t, emit, basicStore, showMessageBox, showMessage, handleShowOverlay, showHeaderTool, tuiRoomClass, roomContentRef, showRoomTool, roomRef, handleHideRoomTool, handleHideRoomToolDebounce, handleHideRoomToolThrottle, showTool, showToolThrottle, hideTool, addRoomContainerEvent, removeRoomContainerEvent, handleRoomContentTap, dismissRoom, leaveRoom, init, createRoom, enterRoom, onRoomNeedPassword, resetStore, logOut, onStartRoom, onJoinRoom, onLeaveRoom, onDismissRoom, onLogout, onKickedOutOfRoom, onUserSigExpired, onKickedOffLine, RoomHeader, RoomFooter, RoomSidebar, RoomContent, RoomSetting, LoadingOverlay, PasswordDialog, AISubtitlesOverlay, RoomInviteOverlay }
}

})